import { IProductCard } from '@src/components/product-card/product-card.types'
import { LoaderWrapper } from '@src/quince-components/src'
import { NAV_POSITION } from '@src/quince-components/src/atoms/swiper/Swiper'
import { getSelectedColorFromSelectedOptions } from '@src/quince-components/src/molecules/productCard/productCard.helpers'
import ProductListSectionTwo from '@src/quince-components/src/molecules/productListSectionTwo'
import { FEATURES, MODULES } from '@src/quince-site-common/src/constants/template-info'
import { PRODUCT_TYPES } from '@src/quince-site-common/src/domains/Product/BundleProduct'
import { ProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant'
import productHelper from '@src/quince-site-common/src/helpers/productHelper'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import useFetch from '@src/quince-site-common/src/hooks/useFetch'
import { useTrackIsElementVisible } from '@src/quince-site-common/src/hooks/useTrackIsElementVisible'
import { useZipCode } from '@src/quince-site-common/src/hooks/zipcode'
import pdpStateManager from '@src/quince-site-common/src/redux/globalStateManager/PDPStateManager'
import { useAppSelector } from '@src/quince-site-common/src/redux/hooks'
import productRecommendationService from '@src/quince-site-common/src/services/productRecommendationService'
import { captureException } from '@src/quince-site-common/src/utils/sentryUtil'
import { RECOMMENDED_SECTION_IDS } from '@src/quince-site-pdp/src/contexts/productPageContext.constants'
import BundlePageSectionProps from '@src/quince-site-pdp/src/domains/BundlePageSectionProps'
import { useIsNewRecommendationsDesign } from '@src/quince-site-pdp/src/templates/productPage/productPage.utils'
import { FINDING_METHOD_AGGREGATE } from '@src/telemetry/findingMethod'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'
import classnames from 'classnames'
import { useTranslation } from 'next-i18next'
import { useCallback, useEffect, useMemo } from 'react'

import { productContextInitialState } from '../../../contexts/ProductPageContext'
import ProductPageSectionProps from '../../../domains/ProductPageSectionProps'
import useProductPageContext from '../../../hooks/useProductContext'
import { getSelectedColor } from '../utils'

import { getTrackingMetaData } from './FrequentlyBoughtTogether.constants'
import styles from './frequentlyBoughtTogether.module.scss'

const FrequentlyBoughtTogether = (props: ProductPageSectionProps | BundlePageSectionProps) => {
  const { t } = useTranslation('common')
  const { zipCode } = useZipCode()
  const {
    recommendationUIData: { [RECOMMENDED_SECTION_IDS.SLOT3]: filteredFbtProducts },
    isRecommendationLoading,
    updateRecommendationAPIData,
  } = useProductPageContext()
  const product = 'bundle' in props ? props.bundle : props.product
  const deviceInfo = useDeviceInfo()
  const isDesktop = deviceInfo.deviceType === DEVICE_TYPES.DESKTOP
  const shouldShowNewFBTSection = useIsNewRecommendationsDesign()
  const isNewFBTMobile = shouldShowNewFBTSection && !isDesktop

  const selectedOptions = useAppSelector<ProductVariantOption[]>(pdpStateManager, pdpStateManager.getSelectedOptions)
  const productId = useAppSelector<string | undefined>(pdpStateManager, pdpStateManager.getProductIdFromRedux)
  const {
    fetchData: fetchFBTProducts,
    isLoading: isFBTLoading,
    response: fbtProducts,
  } = useFetch<IProductCard[], any>(productRecommendationService.getFBTProducts.bind(productRecommendationService), {
    onlyReturnLatestFetch: true,
  })

  const selectedColor = getSelectedColor(selectedOptions)

  useEffect(() => {
    if (!selectedColor || productId !== product.id.toString()) {
      return
    }
    let shopifyProductId
    if (product.type === PRODUCT_TYPES.BUNDLE) {
      shopifyProductId =
        product.customBundleProducts?.[0] &&
        productHelper.getShopifyProductId(product.customBundleProducts[0], selectedOptions)
    } else {
      shopifyProductId = productHelper.getShopifyProductId(product, selectedOptions)
    }

    fetchFBTProducts(shopifyProductId, selectedColor, {
      viewContextGender: product.gender,
      useLite: true,
      limit: 40,
      zipcode: zipCode,
    })
      .then(res => {
        updateRecommendationAPIData(RECOMMENDED_SECTION_IDS.SLOT3, {
          ...productContextInitialState.recommendationUIData[RECOMMENDED_SECTION_IDS.SLOT3],
          data: res,
        })
      })
      .catch((error: unknown) => {
        captureException(error, {
          module: MODULES.PRODUCT,
          feature: FEATURES.FBT,
        })
      })
  }, [selectedColor, productId])

  const isLoadedWithData = !isRecommendationLoading && !isFBTLoading && filteredFbtProducts.data.length
  const isLoadedWithNoData =
    !isFBTLoading && !isRecommendationLoading && fbtProducts?.length === 0 && filteredFbtProducts.data.length === 0

  const displayTitle = useMemo(() => filteredFbtProducts.module_title, [])

  const getIsVisibleTrackParams = useCallback(() => {
    const productId = product.id
    const selectedColor = selectedOptions.filter(o => o.name == 'Color')[0]?.value
    return {
      // @i18n-ignore
      action: 'frequently_purchased_together',
      value: true,
      context: {
        productId,
        selectedColor,
        ...getTrackingMetaData(displayTitle).context,
      },
    }
  }, [product.id, selectedOptions.filter(o => o.name == 'Color')[0]?.value])

  const getRecommendationVisibleTrackParams = useCallback(() => {
    trackEvent(TELEMETRY_EVENT_NAMES.RECOMMENDATION_VIEW, filteredFbtProducts)
  }, [product.id, filteredFbtProducts])

  const { ref: trackIsVisibleRef } = useTrackIsElementVisible(
    getIsVisibleTrackParams,
    getRecommendationVisibleTrackParams,
  )

  if (isLoadedWithNoData || (!isRecommendationLoading && !filteredFbtProducts.data.length)) return null

  const carouselArrowClickTracker = ({ direction }: { direction: string }) => {
    const clickDirectionEvent =
      direction === 'prev' ? TELEMETRY_EVENT_NAMES.CAROUSEL_LEFT_CLICK : TELEMETRY_EVENT_NAMES.CAROUSEL_RIGHT_CLICK
    trackEvent(clickDirectionEvent, {
      module_name: filteredFbtProducts.module_name,
      module_id: filteredFbtProducts.module_id,
    })
  }

  const additionalProductTrackingContext = () => {
    return {
      source_product_id: product.shopifyProductId,
      source_product_color: getSelectedColorFromSelectedOptions(selectedOptions),
      isRecommendationProduct: true,
    }
  }

  const filteredFbtProductsFinal = filteredFbtProducts.data.length && filteredFbtProducts.data

  return (
    <div
      ref={isLoadedWithData ? trackIsVisibleRef : undefined}
      className={classnames(
        styles.frequentlyBoughtTogether,
        isNewFBTMobile && styles.frequentlyBoughtTogether__newDesign,
        !isRecommendationLoading && !filteredFbtProducts.data.length && styles.frequentlyBoughtTogether__hideFbt,
      )}
    >
      {!!isLoadedWithData && (
        <>
          {!!isLoadedWithData && (
            <h2 className={styles.frequentlyBoughtTogether__title}>{t(filteredFbtProducts.module_title_key)}</h2>
          )}
          <LoaderWrapper showLoader={isRecommendationLoading}>
            <>
              {
                <ProductListSectionTwo
                  productCards={filteredFbtProductsFinal || []}
                  wrapperClassName={styles.productListSection}
                  productCardDesktopClassName={styles.productListSection__desktopProductCard}
                  productCardMobileClassName={styles.productListSection__mobileProductCard}
                  productCardWrapperMobileClassName={styles.productListSection__productCardWrapperMobile}
                  hideProductOptions={true}
                  hideProductReviews={true}
                  skipTagRefresh={true}
                  navPosition={NAV_POSITION.RIGHT}
                  // @i18n-ignore
                  tracker={'frequently_purchased_together_products'}
                  productClickTrackConfig={{
                    trackInternal: true,
                    // @i18n-ignore
                    category: 'frequently_purchased_together',
                    // @i18n-ignore
                    action: 'click_frequently_purchased_product',
                    context: {
                      source_product_id: product.shopifyProductId,
                      source_product_color: getSelectedColorFromSelectedOptions(selectedOptions),
                      ...getTrackingMetaData(displayTitle).context,
                      findingMethod: FINDING_METHOD_AGGREGATE.RECOMMENDATION,
                    },
                  }}
                  trackingMetaData={getTrackingMetaData(displayTitle)}
                  additionalProductTrackingContext={additionalProductTrackingContext}
                  numberOfSlidesToScroll={isDesktop ? 4 : 1}
                  carouselArrowClickTracker={carouselArrowClickTracker}
                  chunkSize={shouldShowNewFBTSection ? 9 : undefined}
                  isNewDesign={shouldShowNewFBTSection}
                  hideWishlistIcon={isNewFBTMobile}
                  productPriceClass={isNewFBTMobile ? styles.productPriceClassNewDesign : undefined}
                  productCardLinkClass={isNewFBTMobile ? styles.productCardLink : undefined}
                  productCardImageSectionClass={isNewFBTMobile ? styles.productCardImageSectionClass : undefined}
                  showDots={isNewFBTMobile ? true : undefined}
                />
              }
            </>
          </LoaderWrapper>
        </>
      )}
    </div>
  )
}

export default FrequentlyBoughtTogether
